import PortStepsGridRenderer from 'modules/Editor/components/FlowCore/components/PortStepsGridRenderer';
import { ReactElement, useMemo } from 'react';

import {
  computeControlPoints,
  computePosListFrom2Points,
  parsePosListToCurvedPath,
} from '../CurvedLineWidget/CurvedLineWidget.helpers';
import { DEFAULT_STROKE_SELECTION_TARGET, MIN_WIDGET_SIZE } from '../LineWidget.config';
import { calculateDeltaPoint, getDashArray, getPosListWithPadding, getStrokeLineCap } from '../LineWidget.helpers';
import { CommonLineProps } from '../LineWidget.types';
import { LineMarkerDefs, LineMarkers } from './LineMarkers';
import { DEFAULT_LINE_END_PADDING_CONST, LINE_END_PADDING_CONST_MAP } from './LineRender.config';
import { useLineRender } from './LineRender.hooks';
import { useSteppedLinePosList } from './hooks/useSteppedLinePosList';

const isDebugMode = false;

export const Curved = ({
  id,
  widthPx = MIN_WIDGET_SIZE,
  heightPx = MIN_WIDGET_SIZE,
  posList,
  strokeWidth,
  strokeColor,
  strokeDashType,
  startArrowStyle,
  endArrowStyle,
  isReadonly = false,
  startPort,
  endPort,
  delta,
  leftPx = 0,
  topPx = 0,
  realTimeStartRect,
  realTimeEndRect,
  realTimeStartPort,
  realTimeEndPort,
  isUseRealTimeStartRect,
  isUseRealTimeEndRect,
  fitSvgToPathSize = true,
}: CommonLineProps): ReactElement => {
  const computeNewPosList = useSteppedLinePosList({
    startPort,
    endPort,
    leftPx,
    topPx,
    delta,
    posList,
    realTimeStartRect,
    realTimeEndRect,
    realTimeStartPort,
    realTimeEndPort,
    isUseRealTimeStartRect,
    isUseRealTimeEndRect,
  });

  const newPosList = useMemo(() => {
    // If one end is not connected to a widget, render a normal curve from start to end, otherwise use stepped path
    if ((!startPort || !endPort) && !realTimeStartPort && !realTimeEndPort) {
      const newPos =
        delta?.targetIndex !== undefined && delta?.targetIndex >= 0 ? calculateDeltaPoint(posList, delta) : posList;
      return computePosListFrom2Points(newPos[0], newPos[1]);
    } else {
      return computeNewPosList();
    }
  }, [computeNewPosList, posList, delta, startPort, endPort, realTimeStartPort, realTimeEndPort]);

  // Control points for generating curves - computed from newPosList
  const controlPoints = useMemo(() => computeControlPoints(newPosList), [newPosList]);

  const path = useMemo(() => {
    return parsePosListToCurvedPath(controlPoints);
  }, [controlPoints]);

  const pathWithPadding = useMemo(() => {
    const startDistance = strokeWidth * (LINE_END_PADDING_CONST_MAP[startArrowStyle] ?? DEFAULT_LINE_END_PADDING_CONST);
    const endDistance = strokeWidth * (LINE_END_PADDING_CONST_MAP[endArrowStyle] ?? DEFAULT_LINE_END_PADDING_CONST);
    const result = getPosListWithPadding(newPosList, startDistance, endDistance);
    return parsePosListToCurvedPath(computeControlPoints(result));
  }, [newPosList, startArrowStyle, endArrowStyle, strokeWidth]);

  const dashArray = useMemo(() => {
    return getDashArray(strokeDashType, strokeWidth);
  }, [strokeDashType, strokeWidth]);

  const strokeLineCap = useMemo(() => {
    return getStrokeLineCap(strokeDashType);
  }, [strokeDashType]);

  const { svgWidth, svgHeight, offset } = useLineRender({
    widthPx,
    heightPx,
    newPosList: controlPoints,
    strokeWidth,
    startArrowStyle,
    endArrowStyle,
    fitSvgToPathSize,
  });

  return (
    <>
      {isDebugMode && <PortStepsGridRenderer realTimeStartRect={realTimeStartRect} realTimeEndRect={realTimeEndRect} />}
      <svg
        style={{
          width: `${svgWidth}px`,
          height: `${svgHeight}px`,
          overflow: 'visible',
          position: 'absolute',
          ...(fitSvgToPathSize && {
            top: `${0 - offset.y}px`,
            left: `${0 - offset.x}px`,
          }),
        }}
        data-testid='line-widget-renderer'
      >
        <g transform={`translate(${offset.x},${offset.y})`}>
          {/* Arrow defs */}
          <LineMarkerDefs
            startArrowStyle={startArrowStyle}
            endArrowStyle={endArrowStyle}
            strokeColor={strokeColor}
            id={id}
          />

          {/* Line */}
          <path
            d={pathWithPadding}
            stroke={strokeColor}
            fill='transparent'
            strokeWidth={`${strokeWidth}px`}
            strokeDasharray={dashArray}
            strokeLinecap={strokeLineCap}
          ></path>

          {/* Arrow */}
          <LineMarkers
            startArrowStyle={startArrowStyle}
            endArrowStyle={endArrowStyle}
            id={id}
            path={path}
            strokeWidth={strokeWidth}
          />
          {/* Selection area */}
          {!isReadonly && (
            <path
              id={id}
              pointerEvents='stroke'
              d={path}
              stroke='transparent'
              fill='transparent'
              strokeWidth={`${
                strokeWidth > DEFAULT_STROKE_SELECTION_TARGET ? strokeWidth : DEFAULT_STROKE_SELECTION_TARGET
              }px`}
              strokeDasharray={dashArray}
              strokeLinecap={strokeLineCap}
            ></path>
          )}
        </g>
      </svg>
    </>
  );
};

export default Curved;
